import { createApp } from "vue";
import { createPinia } from "pinia";
import App from "./App.vue";
import { router } from "@/router";
import { useAuthStore } from "@/stores/auth";
import { devSignIn } from "@/composables/useAuth";
import { setClientOverride } from "@/composables/useApi";
import { takeDestination } from "@/lib/post-signin";
import { applyTheme, initialTheme } from "@/lib/theme";
import { redirectUri } from "@/lib/runtime-config";
import "@fontsource-variable/inter";
import "./styles/main.css";

async function bootstrap() {
  const app = createApp(App);
  const pinia = createPinia();
  app.use(pinia);

  applyTheme(initialTheme());

  // Auth init handles ?code= return from Cognito and silent refresh.
  // Must complete BEFORE we register the router — otherwise the router's
  // initial navigation fires (with auth.isSignedIn=false) during our await
  // for the token exchange, the home guard redirects to /signin, and even
  // though the token lands a moment later we're already stuck on /signin.
  const auth = useAuthStore();
  if (import.meta.env.DEV && import.meta.env.VITE_MOCK_API === "1") {
    // Local demo against fixture data: no Cognito, no API (see src/api/mock).
    window.__RUSHD_CONSOLE_CONFIG__ = {
      apiEndpoint: "mock://", cognitoDomain: "mock", cognitoClientId: "mock",
      cognitoRedirectUri: window.location.origin, region: "eu-west-1", allowedEmailDomain: "foodics.com",
    };
    devSignIn({ email: "amine.aitelharraj@foodics.com", "custom:role": "admin", "custom:projects": "*" });
    const { MockApiClient } = await import("@/api/mock/client");
    setClientOverride(new MockApiClient());
  } else if (import.meta.env.DEV && import.meta.env.VITE_LOCAL_API) {
    // Local verification against real data: a loopback server runs the
    // console Lambda in-process with the developer's own AWS credentials.
    window.__RUSHD_CONSOLE_CONFIG__ = {
      apiEndpoint: import.meta.env.VITE_LOCAL_API, cognitoDomain: "local", cognitoClientId: "local",
      cognitoRedirectUri: window.location.origin, region: "eu-west-1", allowedEmailDomain: "foodics.com",
    };
    devSignIn({ email: "amine.aitelharraj@foodics.com", "custom:role": "admin", "custom:projects": "*" });
  } else {
    try {
      await auth.init();
    } catch (err) {
      console.error("auth init failed:", err);
    }
  }

  app.use(router);
  await router.isReady();
  const destination = auth.isSignedIn ? takeDestination() : null;
  if (destination) await router.replace(destination);
  else if (router.currentRoute.value.path === new URL(redirectUri()).pathname
    && ("code" in router.currentRoute.value.query || "state" in router.currentRoute.value.query)) {
    // The router read the URL before auth.init() cleaned Cognito's single-use code from it.
    // Only on Cognito's return path: GitHub's code on /onboarding/github/created is that page's.
    const { code: _c, state: _s, ...rest } = router.currentRoute.value.query;
    await router.replace({ path: router.currentRoute.value.path, query: rest, hash: router.currentRoute.value.hash });
  }
  app.mount("#app");
}

bootstrap();
